import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { MonthlyExtras } from '../types/api';
import { Card, TabBar, Button, Alert } from '@thegpvc/ui';
import { api } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { PageContainer } from '../components/PageContainer';
import { text, cx, table, colWidth } from '../styles/theme';
import { formatDuration, formatNumber, formatTimestamp, formatUsd } from '../utils/format';
import { TOTAL_TOKENS_LABEL, TOTAL_TOKENS_TOOLTIP } from '../utils/tokenLabels';
import { reportPollInterval } from '../utils/reportPolling';
import { errorMessage } from '../utils/errorMessage';
import type { InsightsReport, TimePeriod } from '../types/api';

const REPORTS_PAGE_SIZE = 10;

const periods: { id: TimePeriod; label: string }[] = [
  { id: '7days', label: 'This Week' },
  { id: '30days', label: 'This Month' },
];

const STATUS_COLORS: Record<string, string> = {
  completed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  processing: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
  failed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
};

const PRIORITY_COLORS: Record<string, string> = {
  high: 'text-red-600 dark:text-red-400',
  medium: 'text-amber-600 dark:text-amber-400',
  low: 'text-gray-500 dark:text-gray-400',
};

function StatusBadge({ status }: { status: string }) {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span className={cx('inline-block px-2 py-0.5 rounded-full text-xs font-medium', colors)}>
      {status}
    </span>
  );
}

function formatPeriodRange(start: string, end: string): string {
  const s = parseInt(start, 10);
  const e = parseInt(end, 10);
  if (!s || !e) return '—';
  const sd = new Date(s * 1000);
  const ed = new Date(e * 1000);
  // Show the year when either endpoint isn't the current year (so a report
  // from a prior year — or one straddling a year boundary — reads "Jun 14,
  // 2024" rather than a bare "Jun 14" that's ambiguous with this year). Mirrors
  // formatTimestamp's current-year elision in utils/format.
  const currentYear = new Date().getFullYear();
  const showYear = sd.getFullYear() !== currentYear || ed.getFullYear() !== currentYear;
  const fmt = (d: Date) =>
    d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: showYear ? 'numeric' : undefined,
    });
  return `${fmt(sd)} – ${fmt(ed)}`;
}

function KeyDelta({ label, cur, prev, unit, lowerIsBetter }: { label: string; cur?: number; prev?: number; unit?: string; lowerIsBetter?: boolean }) {
  const na = cur === undefined || cur === -1;
  const hasPrev = prev !== undefined && prev !== -1 && prev !== 0;
  const deltaPct = !na && hasPrev ? ((cur - prev) / prev) * 100 : undefined;
  // Color and sign track the DISPLAYED (rounded) magnitude, not the raw delta:
  // a flat metric (cur === prev) or a sub-0.5% move rounds to 0% and must read
  // as "no change" in neutral gray — rendering it rose implies a decline that
  // didn't happen, and a green "+0%" for a +0.3% move contradicts what's shown.
  const rounded = deltaPct !== undefined ? Math.round(deltaPct) : undefined;
  const flat = rounded === 0;
  const improved =
    deltaPct !== undefined && !flat && (lowerIsBetter ? deltaPct < 0 : deltaPct > 0);
  return (
    <div>
      <div className={cx('text-xs font-medium uppercase', text.secondary)}>{label}</div>
      <div className={cx('text-2xl font-semibold tabular-nums', text.primary)}>
        {na ? '—' : `${cur.toFixed(1)}${unit ?? ''}`}
      </div>
      {rounded !== undefined && (
        <div
          className={cx(
            'text-xs font-medium tabular-nums',
            flat
              ? text.secondary
              : improved
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-rose-600 dark:text-rose-400',
          )}
        >
          {flat ? '±0%' : `${rounded > 0 ? '+' : ''}${rounded}%`} vs prior month
        </div>
      )}
    </div>
  );
}

function fmtReportDay(ts?: string): string {
  const n = parseInt(ts || '0', 10);
  if (!n) return '—';
  return new Date(n * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * MyMonthSections renders the monthly composition: the four Agent Keys with
 * month-over-month movement, the money story, fixes shipped (patterns that
 * recurred then stopped), and exemplar sessions.
 */
function MyMonthSections({ extras }: { extras: MonthlyExtras }) {
  const resolved = extras.resolved ?? [];
  const exemplars = extras.exemplars ?? [];
  return (
    <>
      <Card>
        <Card.Header><span className={text.primary}>Agent Keys</span></Card.Header>
        <Card.Body>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <KeyDelta label="Delegation" cur={extras.delegationCur} prev={extras.delegationPrev} unit="/day" />
            <KeyDelta label="Leverage" cur={extras.leverageCur} prev={extras.leveragePrev} />
            <KeyDelta label="Intervention" cur={extras.interventionCur} prev={extras.interventionPrev} lowerIsBetter />
            <KeyDelta label="Recovery" cur={extras.recoveryCur} prev={extras.recoveryPrev} unit="%" />
          </div>
        </Card.Body>
      </Card>

      {(extras.spendUsd ?? 0) > 0 && (
        <Card>
          <Card.Header><span className={text.primary}>The Money</span></Card.Header>
          <Card.Body>
            <p className={text.primary}>
              <span className="font-semibold tabular-nums">{formatUsd(extras.spendUsd ?? 0)}</span> of
              API-equivalent work delivered
              {(extras.prevSpendUsd ?? 0) > 0 && (
                <span className={text.secondary}> (prior month {formatUsd(extras.prevSpendUsd ?? 0)})</span>
              )}
              .
            </p>
            <p className={cx('mt-1 text-sm', text.secondary)}>
              {formatUsd(extras.recoverableUsd ?? 0)} recoverable in loops &amp; re-reads
              {(extras.continuityUsd ?? 0) > 0 && (
                <> · {formatUsd(extras.continuityUsd ?? 0)} spent keeping long sessions alive (continuity, not waste)</>
              )}
              .
            </p>
          </Card.Body>
        </Card>
      )}

      {resolved.length > 0 && (
        <Card>
          <Card.Header><span className={text.primary}>Fixes Shipped</span></Card.Header>
          <Card.Body>
            <ul className="space-y-3">
              {resolved.map((r, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-emerald-400 dark:bg-emerald-500" />
                  <div>
                    <span className={text.primary}>{r.title}</span>
                    <p className={cx('text-sm', text.secondary)}>
                      Recurred {r.occurrences ?? 0} times, then stopped — quiet since {fmtReportDay(r.lastSeen)}.
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}

      {exemplars.length > 0 && (
        <Card>
          <Card.Header><span className={text.primary}>Best Sessions</span></Card.Header>
          <Card.Body>
            <ul className="space-y-2">
              {exemplars.map((e) => (
                <li key={e.sessionId} className="flex items-baseline gap-2 text-sm">
                  <Link to={`/sessions/${e.sessionId}`} className={cx('font-medium hover:underline', text.link)}>
                    {/* Untitled exemplars fall back to the linked short id, not
                        a bare "Untitled session" identical for every one (#748). */}
                    {e.title || `Session ${e.sessionId.slice(0, 8)}`}
                  </Link>
                  <span className={text.secondary}>{e.project}</span>
                  <span className={cx('ml-auto tabular-nums', text.secondary)}>
                    {(e.toolsPerPrompt ?? 0).toFixed(1)} tools/prompt · {e.filesChanged ?? 0} files · zero friction
                  </span>
                </li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}
    </>
  );
}

function ReportDetail({ report }: { report: InsightsReport }) {
  // Non-completed reports have no meaningful corpus data yet (or never will).
  // Rendering the stats grid full of zeros is misleading, so show a clear,
  // status-specific state instead of the regular detail sections.
  if (report.status === 'failed') {
    return (
      <Alert variant="error">
        Report generation failed.
        {report.errorMessage ? ` ${report.errorMessage}` : ''}
      </Alert>
    );
  }

  if (report.status === 'processing' || report.status === 'pending') {
    const message =
      report.status === 'processing'
        ? 'This report is being generated. It will appear here once complete.'
        : 'This report is queued and will start generating shortly.';
    return (
      <Card>
        <Card.Body>
          <div className="flex items-center gap-3 py-6">
            <span
              className="h-4 w-4 rounded-full border-2 border-orange-400 border-t-transparent animate-spin flex-shrink-0"
              aria-hidden="true"
            />
            <span className={text.secondary}>{message}</span>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {report.kind === 'monthly' && (
        <div className="flex items-center justify-between print:hidden">
          <span className={cx('text-sm font-medium uppercase tracking-wider', text.secondary)}>
            My Month with the Agent · {formatPeriodRange(report.periodStart, report.periodEnd)}
          </span>
          <Button variant="secondary" size="sm" onClick={() => window.print()}>
            Print / Save PDF
          </Button>
        </div>
      )}

      {/* Corpus Shape — summary stats */}
      <Card>
        <Card.Header><span className={text.primary}>Corpus Shape</span></Card.Header>
        <Card.Body>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <div className={cx('text-xs font-medium uppercase', text.secondary)}>Sessions</div>
              <div className={cx('text-2xl font-semibold tabular-nums', text.primary)}>{report.sessionCount}</div>
            </div>
            <div>
              <div
                className={cx('text-xs font-medium uppercase cursor-help', text.secondary)}
                title={TOTAL_TOKENS_TOOLTIP}
              >
                {TOTAL_TOKENS_LABEL}
              </div>
              <div className={cx('text-2xl font-semibold tabular-nums', text.primary)}>{formatNumber(report.totalTokens)}</div>
            </div>
            <div>
              <div className={cx('text-xs font-medium uppercase', text.secondary)}>Duration</div>
              <div className={cx('text-2xl font-semibold tabular-nums', text.primary)}>{formatDuration(report.totalDurationMs)}</div>
            </div>
            <div>
              <div className={cx('text-xs font-medium uppercase', text.secondary)}>Autonomy</div>
              <div className={cx('text-2xl font-semibold tabular-nums', text.primary)}>
                {report.avgAutonomy ? `${(report.avgAutonomy * 100).toFixed(0)}%` : '—'}
              </div>
            </div>
            <div>
              <div className={cx('text-xs font-medium uppercase', text.secondary)}>Turns</div>
              <div className={cx('text-2xl font-semibold tabular-nums', text.primary)}>{formatNumber(report.totalTurns)}</div>
            </div>
            <div>
              <div className={cx('text-xs font-medium uppercase', text.secondary)}>Tool Calls</div>
              <div className={cx('text-2xl font-semibold tabular-nums', text.primary)}>{formatNumber(report.totalToolCalls)}</div>
            </div>
            <div>
              <div className={cx('text-xs font-medium uppercase', text.secondary)}>Files Modified</div>
              <div className={cx('text-2xl font-semibold tabular-nums', text.primary)}>{report.filesModified}</div>
            </div>
            <div>
              <div className={cx('text-xs font-medium uppercase', text.secondary)}>Trigger</div>
              <div className={cx('text-sm font-medium', text.primary)}>{report.trigger || '—'}</div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {report.kind === 'monthly' && report.extras && <MyMonthSections extras={report.extras} />}

      {/* Coach's Note: 1-3 evidenced takeaways + one experiment */}
      {((report.observations && report.observations.length > 0) || report.experiment) && (
        <Card>
          <Card.Header><span className={text.primary}>Coach&apos;s Note</span></Card.Header>
          <Card.Body>
            {report.observations && report.observations.length > 0 && (
              <ul className="space-y-3">
                {report.observations.map((o, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span
                      className={cx(
                        'mt-1.5 h-2 w-2 flex-shrink-0 rounded-full',
                        o.kind === 'win' && 'bg-green-400 dark:bg-green-500',
                        o.kind === 'friction' && 'bg-red-400 dark:bg-red-500',
                        (!o.kind || o.kind === 'habit') && 'bg-blue-400 dark:bg-blue-500',
                      )}
                    />
                    <div>
                      <span className={text.primary}>{o.text}</span>{' '}
                      {o.evidenceSessionId && (
                        <Link to={`/sessions/${o.evidenceSessionId}`} className={cx('text-sm font-medium', text.link)}>
                          see session →
                        </Link>
                      )}
                      {!o.evidenceSessionId && o.evidencePatternId && o.evidencePatternId !== '0' && (
                        <Link to="/insights" className={cx('text-sm font-medium', text.link)}>
                          see pattern →
                        </Link>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {report.experiment && (
              <div className="mt-4 flex items-start gap-2 rounded-md border border-orange-100 bg-orange-50 px-3 py-2 dark:border-orange-900/30 dark:bg-orange-900/15">
                <p className="text-sm">
                  <span className="font-medium text-orange-800 dark:text-orange-300">This week&apos;s experiment: </span>
                  <span className={text.secondary}>{report.experiment}</span>
                </p>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Tool Mix */}
      {report.toolUsage && report.toolUsage.length > 0 && (
        <Card>
          <Card.Header><span className={text.primary}>Tool Mix</span></Card.Header>
          <Card.Body>
            <div className="space-y-2">
              {report.toolUsage.map((tool) => {
                const maxCalls = Math.max(...report.toolUsage.map((t) => parseInt(t.totalCalls, 10) || 0));
                const calls = parseInt(tool.totalCalls, 10) || 0;
                const pct = maxCalls > 0 ? (calls / maxCalls) * 100 : 0;
                return (
                  <div key={tool.name} className="flex items-center gap-3">
                    <span className={cx('text-sm truncate', colWidth.labelSm, text.primary)}>{tool.name}</span>
                    <div className="flex-1 h-5 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden">
                      <div
                        className="h-full bg-orange-400 dark:bg-orange-500 rounded"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className={cx('text-sm tabular-nums text-right', colWidth.valueMd, text.secondary)}>
                      {formatNumber(tool.totalCalls)}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Top Projects */}
      {report.topProjects && report.topProjects.length > 0 && (
        <Card>
          <Card.Header><span className={text.primary}>Top Projects</span></Card.Header>
          <Card.Body>
            <div className="space-y-2">
              {report.topProjects.map((p, i) => (
                <div key={p.name} className="flex items-center gap-3">
                  <span className={cx('text-sm font-medium w-5 text-right tabular-nums', text.secondary)}>{i + 1}.</span>
                  <span className={cx('flex-1', text.primary)}>{p.name}</span>
                  <span className={cx('text-sm tabular-nums', text.secondary)}>
                    {p.sessionCount} session{p.sessionCount !== 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Strengths */}
      {report.strengths && report.strengths.length > 0 && (
        <Card>
          <Card.Header><span className={text.primary}>Strengths</span></Card.Header>
          <Card.Body>
            <ul className="space-y-3">
              {report.strengths.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-1.5 h-2 w-2 rounded-full bg-green-400 dark:bg-green-500 flex-shrink-0" />
                  <div>
                    <span className={text.primary}>{item.text}</span>
                    {item.category && (
                      <span className={cx('ml-2 inline-block px-1.5 py-0.5 rounded text-xs', 'bg-gray-100 dark:bg-gray-800', text.secondary)}>
                        {item.category}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}

      {/* Friction */}
      {report.friction && report.friction.length > 0 && (
        <Card>
          <Card.Header><span className={text.primary}>Friction Points</span></Card.Header>
          <Card.Body>
            <ul className="space-y-3">
              {report.friction.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-1.5 h-2 w-2 rounded-full bg-red-400 dark:bg-red-500 flex-shrink-0" />
                  <div>
                    <span className={text.primary}>{item.text}</span>
                    {item.signal && (
                      <span className={cx('ml-2 inline-block px-1.5 py-0.5 rounded text-xs', 'bg-red-50 dark:bg-red-900/20', 'text-red-600 dark:text-red-400')}>
                        {item.signal}
                      </span>
                    )}
                    {item.count > 0 && (
                      <span className={cx('ml-1 text-xs', text.secondary)}>({item.count}x)</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}

      {/* Recommendations */}
      {report.recommendations && report.recommendations.length > 0 && (
        <Card>
          <Card.Header><span className={text.primary}>Recommendations</span></Card.Header>
          <Card.Body>
            <ol className="space-y-3">
              {report.recommendations.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className={cx('text-sm font-medium w-5 text-right tabular-nums flex-shrink-0', text.secondary)}>{i + 1}.</span>
                  <div>
                    <span className={cx(PRIORITY_COLORS[item.priority] || text.primary)}>{item.text}</span>
                    {item.priority && (
                      <span className={cx('ml-2 inline-block px-1.5 py-0.5 rounded text-xs', 'bg-gray-100 dark:bg-gray-800', text.secondary)}>
                        {item.priority}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

type KindFilter = '' | 'weekly' | 'monthly';

export function Reports() {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<TimePeriod>('7days');
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [listOffset, setListOffset] = useState(0);
  const [kindFilter, setKindFilter] = useState<KindFilter>('');

  const { data: listData, isLoading: listLoading, error: listError, isFetching: listFetching } = useQuery({
    queryKey: ['insightsReports', listOffset, kindFilter],
    queryFn: () => api.listInsightsReports({ limit: REPORTS_PAGE_SIZE, offset: listOffset, kind: kindFilter || undefined }),
    refetchInterval: (query) =>
      query.state.data?.reports?.some((r) => r.status === 'processing' || r.status === 'pending')
        ? 5000
        : false,
  });

  const latestCompleted = listData?.reports?.find((r) => r.status === 'completed');
  const activeReportId = selectedReportId || latestCompleted?.id;

  const { data: activeReport, isLoading: reportLoading } = useQuery({
    queryKey: ['insightsReport', activeReportId],
    queryFn: () => api.getInsightsReport(activeReportId!),
    enabled: !!activeReportId,
    // Keep polling the detail view while the viewed report is still generating,
    // so a report opened in the pending/processing state auto-advances to its
    // completed (or failed) layout instead of showing the spinner until a
    // manual refresh.
    refetchInterval: (query) => reportPollInterval(query.state.data),
  });

  const generateMutation = useMutation({
    mutationFn: ({ p, kind }: { p: TimePeriod; kind?: 'weekly' | 'monthly' }) => api.generateInsightsReport(p, kind),
    onSuccess: (data) => {
      // Jump the detail pane to the just-generated report so the user watches
      // it advance pending → processing → completed. Without this the pane
      // keeps showing the previously-latest *completed* report (activeReportId
      // falls back to latestCompleted), so clicking Generate looks like it did
      // nothing until the new report finishes and bubbles to the top of the
      // list. reportPollInterval already polls the pending/processing detail.
      if (data.report?.id) setSelectedReportId(data.report.id);
      queryClient.invalidateQueries({ queryKey: ['insightsReports'] });
    },
  });

  const reports = listData?.reports || [];
  const total = parseInt(listData?.total || '0', 10);
  const hasNextPage = listOffset + REPORTS_PAGE_SIZE < total;
  const hasPrevPage = listOffset > 0;

  if (listError) {
    return (
      <PageContainer>
        <title>Dixie :: Reports</title>
        <PageHeader title="Reports" />
        <Alert variant="error">Failed to load reports: {errorMessage(listError)}</Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <title>Dixie :: Reports</title>
      <div className="print:hidden">
      <PageHeader title="Reports" isFetching={listFetching && !listLoading}>
        <TabBar
          items={periods}
          activeId={period}
          onChange={(id: string) => setPeriod(id as TimePeriod)}
        />
        <Button
          variant="primary"
          size="sm"
          onClick={() => generateMutation.mutate({ p: period })}
          disabled={generateMutation.isPending}
        >
          {generateMutation.isPending ? 'Generating…' : 'Generate Report'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          title="Compose last calendar month: keys, money, fixes shipped, best sessions"
          onClick={() => generateMutation.mutate({ p: period, kind: 'monthly' })}
          disabled={generateMutation.isPending}
        >
          My Month
        </Button>
      </PageHeader>

      </div>

      {/* Surface failures from the generate action — otherwise the button
          silently re-enables and the user has no idea anything went wrong. */}
      {generateMutation.isError && (
        <Alert variant="error">
          Failed to generate report: {errorMessage(generateMutation.error)}
        </Alert>
      )}

      {/* Report detail */}
      {reportLoading && (
        <Card>
          <Card.Body>
            <div className="space-y-4 py-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {!reportLoading && activeReport && (
        <ReportDetail report={activeReport} />
      )}

      {!reportLoading && !activeReport && !listLoading && (
        <Card>
          <Card.Body>
            <p className={cx('text-center py-8', text.secondary)}>
              No reports yet. Generate your first report to see insights.
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Report list table */}
      <Card className="print:hidden">
        <Card.Header>
          <div className="flex items-center justify-between">
            <span className={text.primary}>Report History</span>
            <TabBar
              variant="pill"
              items={[
                { id: '', label: 'All' },
                { id: 'weekly', label: 'Weekly' },
                { id: 'monthly', label: 'Monthly' },
              ]}
              activeId={kindFilter}
              onChange={(id: string) => {
                setListOffset(0);
                setKindFilter(id as KindFilter);
              }}
            />
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {listLoading && (
            <div className="space-y-3 p-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              ))}
            </div>
          )}
          {!listLoading && reports.length === 0 && (
            <p className={cx('text-center py-6', text.secondary)}>No reports generated yet.</p>
          )}
          {!listLoading && reports.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={table.header}>
                    <th className={cx('text-left px-4 py-2 font-medium', table.headerText)}>Period</th>
                    <th className={cx('text-left px-4 py-2 font-medium', table.headerText)}>Trigger</th>
                    <th className={cx('text-left px-4 py-2 font-medium', table.headerText)}>Status</th>
                    <th className={cx('text-right px-4 py-2 font-medium', table.headerText)}>Sessions</th>
                    <th className={cx('text-right px-4 py-2 font-medium', table.headerText)}>Created</th>
                  </tr>
                </thead>
                <tbody className={table.divide}>
                  {reports.map((report) => (
                    <tr
                      key={report.id}
                      onClick={() => setSelectedReportId(report.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedReportId(report.id);
                        }
                      }}
                      tabIndex={0}
                      aria-selected={report.id === activeReportId}
                      aria-label={`View report for ${formatPeriodRange(report.periodStart, report.periodEnd)}, status ${report.status}`}
                      className={cx(
                        'cursor-pointer transition-colors',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange-400',
                        report.id === activeReportId
                          ? 'bg-orange-50 dark:bg-orange-900/20'
                          : table.rowHover,
                      )}
                    >
                      <td className={cx('px-4 py-3', text.primary)}>
                        {formatPeriodRange(report.periodStart, report.periodEnd)}
                        {report.kind === 'monthly' && (
                          <span className="ml-2 inline-block rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                            My Month
                          </span>
                        )}
                      </td>
                      <td className={cx('px-4 py-3', text.secondary)}>
                        {report.trigger || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={report.status} />
                      </td>
                      <td className={cx('px-4 py-3 text-right tabular-nums', text.primary)}>
                        {report.sessionCount}
                      </td>
                      <td className={cx('px-4 py-3 text-right', text.secondary)}>
                        {formatTimestamp(report.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(hasNextPage || hasPrevPage) && (
            <div className="flex justify-between items-center px-4 py-3 border-t border-gray-100 dark:border-gray-800">
              <button
                onClick={() => setListOffset(Math.max(0, listOffset - REPORTS_PAGE_SIZE))}
                disabled={!hasPrevPage}
                aria-label="Previous page"
                className={cx(
                  'text-sm',
                  hasPrevPage ? text.link : cx(text.muted, 'cursor-not-allowed'),
                )}
              >
                Previous
              </button>
              <span className={cx('text-sm tabular-nums', text.secondary)}>
                Showing {listOffset + 1}–{Math.min(listOffset + REPORTS_PAGE_SIZE, total)} of {total}
              </span>
              <button
                onClick={() => setListOffset(listOffset + REPORTS_PAGE_SIZE)}
                disabled={!hasNextPage}
                aria-label="Next page"
                className={cx(
                  'text-sm',
                  hasNextPage ? text.link : cx(text.muted, 'cursor-not-allowed'),
                )}
              >
                Next
              </button>
            </div>
          )}
        </Card.Body>
      </Card>
    </PageContainer>
  );
}
